import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { FetchDeviceQuery, MoveDeviceMut } from '@/lib/api/devices/devices';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SiteSelector } from './SiteSelector';
import { SitesSearchDto } from '@/app/Sites/siteApi';
import { AlertTriangle } from 'lucide-react';

export type MoveDeviceButtonProps = {
  deviceId: string;
  currentProjectId?: string;
};

export const MoveDeviceButton = ({ deviceId, currentProjectId }: MoveDeviceButtonProps) => {
  const [open, setOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState<SitesSearchDto | null>(null);
  const queryClient = useQueryClient();
  const moveMut = useMutation(MoveDeviceMut(deviceId));
  const { toast } = useToast();

  const handleMove = async () => {
    if (!selectedSite) return;

    try {
      await moveMut.mutateAsync({ targetProjectId: selectedSite.id });
      toast({ title: 'Device moved successfully', description: `Moved to ${selectedSite.name}` });
      setOpen(false);
      setSelectedSite(null);
      queryClient.invalidateQueries({
        queryKey: FetchDeviceQuery({ deviceId }).queryKey,
      });
    } catch {
      toast({ title: 'Error moving device', variant: 'destructive' });
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setSelectedSite(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline">Move Device</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Move Device to Different Site</DialogTitle>
          <DialogDescription className="sr-only">Select a new site to move this device.</DialogDescription>
        </DialogHeader>
        <div className="px-6 pb-6 text-sm">
          <div className="border p-4 rounded mb-6">
      
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 shrink-0 text-yellow-500" />
            <p className="text-yellow-500 text-lg">Warning</p>
          </div>
          <p>Moving the device to a new site will:</p>
          <ul className="ml-5 list-disc space-y-1">
            <li>Move all cameras, speakers, and relays on this device to the new site</li>
            <li>Update the associated customer to the new site</li>
          </ul>
        </div>
        </div>

        {/* Form section */}
        <div className="space-y-4  pt-4">
          <div className="space-y-2">
            <Label htmlFor="site-selector" className="text-lg">
              Destination Site
            </Label>
            <SiteSelector
              value={selectedSite?.id}
              onChange={setSelectedSite}
              excludeProjectId={currentProjectId}
            />
          </div>
        </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleMove}
            disabled={!selectedSite || moveMut.isPending}
          >
            {moveMut.isPending ? 'Moving...' : 'Move Device'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
